import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { bindActionCreators, compose } from 'redux';
import { withRouter } from 'react-router';
import { injectIntl } from 'react-intl';

// Components
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowHeader from 'components/molecules/AppointmentFlowHeader';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import VehicleBuilder from 'components/organisms/VehicleBuilder';
import Media from 'react-media';

// CTA COMPONENTS (POC)
import CTAFlowHeader from 'components/molecules/CTAFlowHeader';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Actions
import { goTo } from 'store/reducers/navigation/actions';
import { loadServices } from 'store/reducers/service/actions';
import { gtmIsIDK, gtmUserEvent } from 'store/reducers/gtm/actions';
import {
  setRegDate,
  sendVehicleShown,
  sendVehicleIndicated
} from 'store/reducers/appointment/actions';
import { getApptAdvisorsAndTranspOptions } from 'store/reducers/apptOptions/actions';

// Selectors
import {
  getEngineSize,
  getModel,
  getMileage,
  getYear,
  getVehicleRegDate
} from 'store/reducers/appointment/selectors';
import { hasIDK } from 'store/reducers/trims/selectors';
import { isLoggedIn } from 'store/reducers/user/selectors';
import {
  getShowMobileFooter,
  getAppFlow
} from 'store/reducers/userInteractions/selectors';
import { getShowMileage } from 'store/reducers/dealerSettings/selectors';
import {
  getCTABanner,
  hideBottomCTA,
  showProgressBar
} from 'store/reducers/optimizelyABTest/selectors';

// Constants
import {
  HOME_ROUTE,
  SERVICES_ROUTE,
  APPOINTMENT_ROUTE,
  USER_VEHICLES_ROUTE
} from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { APP_BREAKPOINTS } from 'constants/appConfig';
import { APP_FLOW } from 'constants/appFlow';
import { labelsStepIndicator } from 'constants/CTAConfig';

import './VehiclePage.scss';
import { format } from 'date-fns';
import { resetScrollPosition } from 'utils/DOM';

const ns = 'vehicle-page';
const { FORWARD } = NAVIGATION;
const { QUICK_APPT } = APP_FLOW;

class VehiclePage extends Component {
  constructor(props) {
    super(props);
    // prettier-ignore
    this.vehicleBuilder = React.createRef();
  }

  componentDidMount() {
    resetScrollPosition(0);
    this.props.sendVehicleShown();
  }

  gtmMileage() {
    const { mileage, gtmUserEvent, ctaBanner, appFlow } = this.props;
    if (mileage) {
      gtmUserEvent(
        'Done Button',
        'Clicked',
        'Done selected',
        'GTM_CURRENT_PAGE > Services'
      );
      gtmUserEvent(
        'Mileage Field',
        'Filled',
        'Mileage Filled',
        'GTM_CURRENT_PAGE > Services',
        `${mileage}`
      );
    } else {
      const route = appFlow === QUICK_APPT ? APPOINTMENT_ROUTE : SERVICES_ROUTE;
      gtmUserEvent(
        ctaBanner ? 'NEXT button' : 'Skip Button',
        'Clicked',
        ctaBanner ? `Go to next page through CTA` : `Skip selected`,
        `GTM_CURRENT_PAGE > ${route} page`
      );
    }
  }

  verifyRegDate() {
    const { regDate, year, setRegDate } = this.props;
    if (!regDate) {
      setRegDate(format(new Date(year, 0, 1), 'YYYY-MM-DD'));
    }
  }

  getWizard(isMobile) {
    const {
      model,
      hasIDK,
      engineSize,
      loadServices,
      gtmIsIDK,
      isLoggedIn,
      showMobileFooter,
      intl,
      mileage,
      showMileage,
      ctaBanner,
      hideBottomCTA,
      showProgressBar,
      sendVehicleIndicated
    } = this.props;
    const classProps = classNames(`${ns}__wizard`, {
      [`${ns}--footer-bottom-up`]: showMobileFooter || !isMobile,
      [`${ns}--footer-hidden`]: !showMobileFooter && isMobile
    });
    const footerMessage = {
      id: 'c8.common.continue'
    };
    if (showMobileFooter && isMobile) {
      if (mileage || !showMileage) {
        footerMessage.id = 'c8.footer.next';
      } else {
        footerMessage.id = 'c8.vehicle.page.skip.mileage';
      }
    }

    const ctaLabel = intl.formatMessage({
      id: 'c8.cta.header.back',
      defaultMessage: 'Back'
    });
    const backButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.back.to.homepage',
      defaultMessage: 'Back to homepage'
    });
    const nextButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.continue.to.select.appointment.service',
      defaultMessage: 'Continue to select appointment service'
    });
    return (
      <AppointmentFlowWizard
        className={classProps}
        hideFooter={Boolean(hideBottomCTA)}
        header={() => {
          return ctaBanner !== null ? (
            <CTAFlowHeader
              label={ctaLabel}
              hideServices={true}
              routeTo={isLoggedIn ? USER_VEHICLES_ROUTE : HOME_ROUTE}
              backButtonAriaLabel={backButtonAriaLabel}
              ctaButtonDisabled={!((model && hasIDK) || engineSize)}
              ctaBanner={ctaBanner}
              onClickCtaButton={() => {
                this.verifyRegDate();
                loadServices();
                this.getApptOptions();
                gtmIsIDK('CONTINUE');
                this.gtmMileage();
                sendVehicleIndicated();
              }}
              typeButton={'cta-next'}
              isBookItButtonVisible={false}
              nextButtonAriaLabel={nextButtonAriaLabel}
            />
          ) : (
            <AppointmentFlowHeader
              routeTo={isLoggedIn ? USER_VEHICLES_ROUTE : HOME_ROUTE}
              backButtonAriaLabel={backButtonAriaLabel}
            />
          );
        }}
        footer={() => {
          return !hideBottomCTA ? (
            <AppointmentFlowFooter
              buttonDisabled={!((model && hasIDK) || engineSize)}
              buttonText={intl.formatMessage(footerMessage)}
              className={`${ns}__footer`}
              onClick={() => {
                this.verifyRegDate();
                loadServices();
                this.getApptOptions();
                gtmIsIDK('CONTINUE');
                this.gtmMileage();
                sendVehicleIndicated();
              }}
              ariaLabel={nextButtonAriaLabel}
            />
          ) : null;
        }}
        body={() => (
          <React.Fragment>
            {showProgressBar && (
              <CtaStepIndicator
                htmlId="VehiclePageStepIndicator"
                labels={labelsStepIndicator(intl)}
                activeStep={1}
              />
            )}
            <VehicleBuilder ref={this.vehicleBuilder} />
          </React.Fragment>
        )}
      />
    );
  }

  async getApptOptions() {
    const { appFlow, getApptAdvisorsAndTranspOptions, goTo } = this.props;
    let route;
    if (appFlow === QUICK_APPT) {
      await getApptAdvisorsAndTranspOptions();
      route = APPOINTMENT_ROUTE;
    } else {
      route = SERVICES_ROUTE;
    }
    goTo(route, FORWARD);
  }

  render() {
    return (
      <div className={ns}>
        <Media query={`(max-width: ${APP_BREAKPOINTS.XS}px)`}>
          {matches => this.getWizard(matches)}
        </Media>
      </div>
    );
  }
}

VehiclePage.propTypes = {
  engineSize: PropTypes.string,
  goTo: PropTypes.func.isRequired,
  gtmIsIDK: PropTypes.func.isRequired,
  gtmUserEvent: PropTypes.func,
  hasIDK: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  loadServices: PropTypes.func.isRequired,
  mileage: PropTypes.number,
  model: PropTypes.string,
  regDate: PropTypes.instanceOf(Date),
  setRegDate: PropTypes.func.isRequired,
  sendVehicleShown: PropTypes.func.isRequired,
  sendVehicleIndicated: PropTypes.func.isRequired,
  showMileage: PropTypes.bool,
  showMobileFooter: PropTypes.bool.isRequired,
  appFlow: PropTypes.string,
  year: PropTypes.number,
  ctaBanner: PropTypes.object,
  hideBottomCTA: PropTypes.bool,
  showProgressBar: PropTypes.bool
};

const mapStateToProps = state => {
  return {
    hasIDK: hasIDK(state),
    engineSize: getEngineSize(state),
    model: getModel(state),
    mileage: getMileage(state),
    isLoggedIn: isLoggedIn(state),
    showMileage: getShowMileage(state),
    showMobileFooter: getShowMobileFooter(state),
    appFlow: getAppFlow(state),
    regDate: getVehicleRegDate(state),
    year: getYear(state),
    ctaBanner: getCTABanner(state),
    hideBottomCTA: hideBottomCTA(state),
    showProgressBar: showProgressBar(state)
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      goTo,
      loadServices,
      gtmIsIDK,
      gtmUserEvent,
      setRegDate,
      getApptAdvisorsAndTranspOptions,
      sendVehicleShown,
      sendVehicleIndicated
    },
    dispatch
  );

export default compose(
  withRouter,
  injectIntl,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(VehiclePage);



// WEBPACK FOOTER //
// ./src/components/pages/VehiclePage/VehiclePage.js